import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { MediaImage } from "../MediaImage";
import { useSiteMedia } from "../../lib/siteMedia";
import { formatPrice, UNIT_LABELS, VAT_LABELS, usePublishedPrices } from "../../lib/productPrices";
import { quoteHref } from "../../data/homeContent";
import { trackEvent } from "../../lib/api";
import { SectionHeading } from "../home/CtaButtons";

const MaterialCard = ({ m, media, price, from }) => (
  <li className="card card-hover group flex flex-col overflow-hidden" data-testid={`material-card-${m.href.slice(1)}`}>
    <MediaImage media={media} sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, calc(100vw - 32px)" className="aspect-[4/3]"
      imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
    <div className="flex flex-1 flex-col p-6">
      <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">{m.material}</p>
      <h3 className="h3 mt-2"><Link to={m.href} className="hover:text-maas">{m.title}</Link></h3>
      <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{m.text}</p>
      <ul className="mt-4 flex flex-wrap gap-2">{m.points.map((s) => <li key={s} className="chip"><Check className="h-3 w-3 text-maas" aria-hidden="true" />{s}</li>)}</ul>
      {price && <p className="mt-4 text-[14px] font-semibold text-ink">{formatPrice(price)} <span className="font-normal text-slate-500">{UNIT_LABELS[price.unit]}, {VAT_LABELS[price.vat || "incl"]}</span></p>}
      <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-100 pt-5">
        <Link to={m.href} className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-ink hover:text-maas" data-testid={`material-link-${m.href.slice(1)}`}>
          Bekijk {m.title.toLowerCase()} <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <Link to={quoteHref(m.onderwerp)} state={{ from }} onClick={() => trackEvent("cta_quote_click", { location: `material-${m.onderwerp}` })}
          className="text-[14.5px] font-semibold text-maas hover:text-maas-hover" data-testid={`material-quote-${m.onderwerp}`}>Prijsindicatie</Link>
      </div>
    </div>
  </li>
);

export const MaterialChoice = ({ choice }) => {
  const media = useSiteMedia();
  const prices = usePublishedPrices();
  const { pathname } = useLocation();
  return (
    <section id="vergelijken" aria-labelledby="materials-title" className="section scroll-mt-12" data-testid="overview-materials">
      <div className="container-tight">
        <SectionHeading id="materials-title" eyebrow="Kies uw materiaal" title={choice.title} lead={choice.lead} />
        <ul className={`mt-10 grid gap-6 sm:grid-cols-2 ${choice.items.length > 2 ? "lg:grid-cols-3" : ""}`}>
          {choice.items.map((m) => <MaterialCard key={m.href} m={m} media={media(m.media)} price={prices[m.priceKey]} from={pathname} />)}
        </ul>
      </div>
    </section>
  );
};

const cell = "p-3 pr-4 align-top sm:p-4";

export const CompareTable = ({ compare }) => (
  <section aria-labelledby="compare-title" className="section-tight" data-testid="overview-compare">
    <div className="container-tight">
      <SectionHeading id="compare-title" eyebrow="Vergelijking" title={compare.title} />
      <ul className="mt-8 space-y-4 sm:hidden" data-testid="overview-compare-mobile">
        {compare.columns.map((c, i) => (
          <li key={c} className="card p-5">
            <h3 className="font-display text-[17px] font-bold text-ink">{c}</h3>
            <dl className="mt-3 space-y-2.5">
              {compare.rows.map(([k, ...vals]) => (
                <div key={k}><dt className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-slate-500">{k}</dt><dd className="text-[14.5px] text-slate-600">{vals[i]}</dd></div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
      <div className="card mt-8 hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[560px] text-left text-[14px] sm:text-[15px]" data-testid="overview-compare-table">
          <caption className="sr-only">{compare.title}</caption>
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className={`${cell} w-[22%] text-[11.5px] font-semibold uppercase tracking-[0.12em] text-slate-500`}>Kenmerk</th>
              {compare.columns.map((c) => <th key={c} scope="col" className={`${cell} font-display text-[16px] font-bold text-ink`}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {compare.rows.map(([k, ...vals]) => (
              <tr key={k} className="border-t border-slate-200">
                <th scope="row" className={`${cell} font-medium text-ink`}>{k}</th>
                {vals.map((v, i) => <td key={`${k}-${i}`} className={`${cell} text-slate-600`}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export const ChoiceGuide = ({ guide }) => (
  <section id="keuzehulp" aria-labelledby="guide-title" className="section scroll-mt-12 border-y border-slate-200 bg-slate-50" data-testid="overview-guide">
    <div className="container-tight">
      <SectionHeading id="guide-title" eyebrow="Keuzehulp" title={guide.title} />
      <ul className={`mt-8 grid gap-4 ${guide.items.length > 2 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {guide.items.map((g) => (
          <li key={g.title} className="card p-6">
            <h3 className="text-[17px] font-semibold text-ink">{g.title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{g.text}</p>
            <Link to={g.href} className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-maas hover:text-maas-hover">{g.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
);
